'use client';

import Image from 'next/image';
import { Container } from 'react-bootstrap';
import type { PortableTextBlock } from 'next-sanity';

import Button from '@/componentv2/button/button';
import { LandingPortableText } from '@/components/landingPortableText/landingPortableText';
import VideoPlayer from '@/components/videoPlayer/videoPlayer';
import type { LANDING_OR_PRODUCT_PAGE_QUERY_RESULT } from '@/sanity.types';
import { toEdgeAssetUrl } from '@/sanity/lib/utils';
import { getSectionBackgroundStyle } from '../../utils';
import './CaseStudiesSection.scss';

type LandingPageData = Extract<
  NonNullable<LANDING_OR_PRODUCT_PAGE_QUERY_RESULT>,
  { _type: 'landingPage' }
>;

interface CaseStudiesSectionProps {
  data: NonNullable<LandingPageData['caseStudies']>;
}

export default function CaseStudiesSection({ data }: CaseStudiesSectionProps) {
  const { sectionLabel, title, caseStudyCards, background } = data;

  return (
    <section
      className="rlp-case-studies-section"
      style={getSectionBackgroundStyle(background)}
    >
      <Container>
        {sectionLabel && (
          <span className="rlp-section-label">{sectionLabel}</span>
        )}
        {title && (
          <div className="rlp-section-headline">
            <LandingPortableText value={title as PortableTextBlock[]} />
          </div>
        )}
        {caseStudyCards && (
          <div className="rlp-case-study-card">
            <div className="rlp-case-study-media">
              {caseStudyCards.mediaType === 'video' &&
                caseStudyCards.videoUrl && (
                  <VideoPlayer
                    url={caseStudyCards.videoUrl}
                    width="100%"
                    height="100%"
                    autoplay={false}
                  />
                )}
              {caseStudyCards.mediaType === 'image' &&
                caseStudyCards.image?.fileUrl && (
                  <Image
                    src={toEdgeAssetUrl(caseStudyCards.image.fileUrl) as string}
                    alt={title ? '' : 'Case study'}
                    width={600}
                    height={400}
                  />
                )}
            </div>
            <div className="rlp-case-study-content">
              {caseStudyCards.content && (
                <LandingPortableText
                  value={caseStudyCards.content as PortableTextBlock[]}
                />
              )}
              {caseStudyCards.ctaText && (
                <Button
                  href={caseStudyCards.ctaLink}
                  variant="outline-secondary"
                >
                  {caseStudyCards.ctaText}
                </Button>
              )}
            </div>
          </div>
        )}
      </Container>
    </section>
  );
}
